import { coerceArray, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  Optional,
  ViewEncapsulation
} from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { AutoUnsubscribe } from '@nitro/core/common';
import { NkPickerControl } from '@nitro/reactor/common';

import { DateTimeAdapter } from '../adapters/date-time-adapter';
import { RangeDateTimeAdapter } from '../adapters/range-date-time-adapter';
import { DateRangeType, DateTimeRange } from '../types';

// export function createMissingRangeImplError(provider: string) {
//   return Error(
//       `RangeDateTime: No provider found for ${provider}. You must import one of the following ` +
//       `modules at your application root: MatNativeDateModule, MatMomentDateModule, or provide a `
//       + `custom implementation.`);
// }

type R<D> = DateTimeRange<D>;

export interface NkRangeDateTimeConfig {
  clamp?: boolean;
  swap?: boolean;
  selectOutside?: boolean;
}
export const NK_RANGE_DATETIME_DEFAULT_OPTIONS = new InjectionToken<NkRangeDateTimeConfig>(
  'NK_RANGE_DATETIME_DEFAULT_OPTIONS'
);

@AutoUnsubscribe()
@Component({
  selector: 'nk-range-datetime-base',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NkRangeDateTimeBase<D> extends NkPickerControl<R<D>> implements OnInit {
  @Input()
  get value(): R<D> | null {
    return !this.startDate || !this.endDate ? null : { start: this.startDate, end: this.endDate };
  }
  set value(v: R<D> | null) {
    if (v) {
      this.startDate = v.start;
      this.endDate = v.end;
    } else {
      this.startDate = null;
      this.endDate = null;
    }
    this._changeDetectorRef.markForCheck();
  }

  _startDate: D | null = null;
  @Input()
  get startDate(): D | null {
    return this._startDate;
  }
  set startDate(v: D | null) {
    if (v) {
      this._startDate = v;
    } else {
      this._startDate = null;
    }
    this._changeDetectorRef.markForCheck();
  }

  _endDate: D | null = null;
  @Input()
  get endDate(): D | null {
    return this._endDate;
  }
  set endDate(v: D | null) {
    if (v) {
      this._endDate = v;
    } else {
      this._endDate = null;
    }
    this._changeDetectorRef.markForCheck();
  }

  _disabled: boolean;
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(d: boolean) {
    this._disabled = d;
  }

  /** The maximum selectable date. */
  @Input()
  get max(): D | null {
    return this._maxDate;
  }
  set max(value: D | null) {
    this._maxDate = this._dateAdapter.validOrNull(this._dateAdapter.deserialize(value));
    this._onValidatorChange();
  }

  @Input()
  get min(): D | null {
    return this._minDate;
  }
  set min(value: D | null) {
    this._minDate = this._dateAdapter.validOrNull(this._dateAdapter.deserialize(value));
    this._onValidatorChange();
  }

  private _minDate: D | null;
  private _maxDate: D | null;

  @Input() clamp;
  @Input() swap;
  /** allows range selection of start past end and end before start */
  @Input() selectOutside;

  /** Function that can be used to add custom CSS classes to dates. */
  @Input() dateClass: (range: R<D>, selectedRange?: R<D>) => MatCalendarCellCssClasses;

  /** Function used to filter which dates are selectable. */
  @Input() dateFilter: (range: R<D>) => boolean;

  /** The form control validator for the min date. */
  private _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const startValue = control.value
      ? this._dateAdapter.validOrNull(this._dateAdapter.deserialize(control.value.start))
      : null;
    const endValue = control.value
      ? this._dateAdapter.validOrNull(this._dateAdapter.deserialize(control.value.end))
      : null;
    return (!this.min ||
      !startValue ||
      this._dateAdapter.compareDateTime(this.min, startValue) < 0) &&
      (!this.min || !endValue || this._dateAdapter.compareDateTime(this.min, endValue) <= 0)
      ? null
      : {
          nkRangeDateTimeEditMin: {
            min: this.min,
            actual: { start: startValue, end: endValue }
          }
        };
  };

  /** The form control validator for the max date. */
  private _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const startValue = control.value
      ? this._dateAdapter.validOrNull(this._dateAdapter.deserialize(control.value.start))
      : null;
    const endValue = control.value
      ? this._dateAdapter.validOrNull(this._dateAdapter.deserialize(control.value.end))
      : null;
    return (!this.max ||
      !startValue ||
      this._dateAdapter.compareDateTime(this.max, startValue) >= 0) &&
      (!this.max || !endValue || this._dateAdapter.compareDateTime(this.max, endValue) >= 0)
      ? null
      : {
          nkRangeDateTimeEditMax: {
            max: this.max,
            actual: { start: startValue, end: endValue }
          }
        };
  };

  /** The form control validator for the range order. */
  private _rangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    this._rangeAdapter.isIncomplete(control.value) ||
    this._rangeAdapter.isEmpty(control.value) ||
    this._rangeAdapter.isValidRange(control.value)
      ? null
      : { nkRangeDateTimeEditRange: true };
  /** The combined form control validator for this input. */

  validator: ValidatorFn | null = Validators.compose([
    // eslint-disable-line
    this._rangeValidator,
    this._minValidator,
    this._maxValidator
  ]);

  constructor(
    public _dateAdapter: DateTimeAdapter<D>,
    public _rangeAdapter: RangeDateTimeAdapter<D>,
    public _changeDetectorRef: ChangeDetectorRef,
    @Optional()
    @Inject(NK_RANGE_DATETIME_DEFAULT_OPTIONS)
    public config: NkRangeDateTimeConfig
  ) {
    super();
    this.parser = this._rangeAdapter.parse;
    this.formatter = this._rangeAdapter.format;
    // if (!this._dateAdapter) {
    //   throw createMissingRangeImplError('DateTimeAdapter');
    // }
    // if (!this._rangeAdapter) {
    //   throw createMissingRangeImplError('RangeDateTimeAdapter');
    // }
  }

  _dateClass = (d: D) => {
    const classes = [];

    if (d && this.value && this.value.end && this.value.start) {
      if (
        this._dateAdapter.compareDate(this.value.start, d) <= 0 &&
        this._dateAdapter.compareDate(d, this.value.end) <= 0
      ) {
        classes.push('nk-selected-range');
      }
      if (this._dateAdapter.compareDate(this.value.start, d) === 0) {
        classes.push('nk-selected-range-start');
      }
      if (this._dateAdapter.compareDate(this.value.end, d) === 0) {
        classes.push('nk-selected-range-end');
      }
    }
    return classes;
  };

  _dateClassStart = (d: D): MatCalendarCellCssClasses => {
    let classes = this._dateClass(d);
    if (this.dateClass) {
      classes = [
        ...classes,
        ...coerceArray(
          this.dateClass(
            { start: d, end: null },
            {
              start: this._startDate ? this._startDate : null,
              end: this._endDate ? this._endDate : null
            }
          )
        )
      ];
    }
    return classes;
  };

  _dateClassEnd = (d: D): MatCalendarCellCssClasses => {
    let classes = this._dateClass(d);
    if (this.dateClass) {
      classes = [
        ...classes,
        ...coerceArray(
          this.dateClass(
            { start: null, end: d },
            {
              start: this._startDate ? this._startDate : null,
              end: this._endDate ? this._endDate : null
            }
          )
        )
      ];
    }
    return classes;
  };

  _dateFilterStart = (d: D) => {
    const df = this.dateFilter
      ? this.dateFilter({ start: d, end: this._endDate ? this._endDate : null })
      : true;
    if (!this.selectOutside) {
      return df && (this._endDate ? d <= this._endDate : true);
    } else {
      return df;
    }
  };

  _dateFilterEnd = (d: D) => {
    const df = this.dateFilter
      ? this.dateFilter({
          start: d,
          end: this._startDate ? this._startDate : null
        })
      : true;
    if (!this.selectOutside) {
      return df && (this._startDate ? d >= this._startDate : true);
    } else {
      return df;
    }
  };

  _rangePartChanged(date: D, type: DateRangeType): void {
    if (type === 'start') {
      this._startDate = this.clamp ? this._dateAdapter.clampDate(date, this.min, this.max) : date;
    }
    if (type === 'end') {
      this._endDate = this.clamp ? this._dateAdapter.clampDate(date, this.min, this.max) : date;
    }

    let swp: D;
    if (this.swap) {
      if (this._dateAdapter.compareDateTime(this._startDate, this._endDate) > 0) {
        swp = this._dateAdapter.clone(this._startDate);
        this._startDate = this._dateAdapter.clone(this._endDate);
        this._endDate = this._dateAdapter.clone(swp);
      }
    }

    if (this._startDate && this._endDate) {
      this.valueChange.emit(this.value);
      this._onChange(this.value);
      this.updateControls(this.value);
    }
    this._onValidatorChange();
    this._onTouched();
  }

  ngOnInit(): void {
    this.config = this.config || {};
    this.clamp = coerceBooleanProperty(this.clamp || this.config.clamp);
    this.swap = coerceBooleanProperty(this.swap || this.config.swap);
    this.selectOutside = coerceBooleanProperty(
      this.selectOutside || this.config.selectOutside || true
    );
  }
}
